/**
 * @zendir/ui - LayerControlPanel
 *
 * Floating layer-toggle panel for the 3D viewer (mirrors the
 * `MapLayerDef` pattern from `<GroundTrackMap />`). Three sections:
 *
 *   1. Render flags  — labels / coverage / orbits  (HOW objects draw)
 *   2. Object groups — spacecraft / GS / celestial (WHICH objects draw)
 *   3. Custom layers — app-defined overlays        (caller renders them)
 *
 * The panel is **toggle UI only**. It emits `onChange` callbacks; the
 * consumer wires them to `<ZenSpace3D layers={...} visibility={...} />`
 * (or to its own overlay state for custom layers). Same contract as
 * `MapLayerDef` — keeps the panel reusable and the renderer
 * untouched.
 *
 * ```tsx
 * const [layers, setLayers] = useState({ orbits: true, labels: false, coverage: false });
 * const [visibility, setVisibility] = useState({
 *   spacecraft: true, groundStations: true, celestialBodies: true,
 * });
 * const [customLayers, setCustomLayers] = useState<Record<string, boolean>>({ rf: false });
 *
 * <ZenSpace3D layers={layers} visibility={visibility} ... />
 * <LayerControlPanel
 *   layers={layers}
 *   visibility={visibility}
 *   customLayers={[{ id: 'rf', label: 'RF Coverage' }]}
 *   customLayerState={customLayers}
 *   groupCounts={{ spacecraft: scene.satellites.length, ... }}
 *   onLayersChange={setLayers}
 *   onVisibilityChange={setVisibility}
 *   onCustomLayerChange={(id, on) => setCustomLayers((c) => ({ ...c, [id]: on }))}
 * />
 * ```
 */

import { memo, useCallback } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useThemeTokens } from "../theme/ThemeProvider";
import type { LayerDef } from "../types";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Render-flag layers — passed straight through to `<ZenSpace3D layers={...} />`. */
export interface RenderLayers {
  labels?: boolean;
  coverage?: boolean;
  orbits?: boolean;
}

/** Object-group visibility — passed straight through to `<ZenSpace3D visibility={...} />`. */
export interface GroupVisibility {
  spacecraft?: boolean;
  groundStations?: boolean;
  celestialBodies?: boolean;
}

/** Counts shown in the group toggles ("Spacecraft (5)"). All optional. */
export interface GroupCounts {
  spacecraft?: number;
  groundStations?: number;
  celestialBodies?: number;
}

export interface LayerControlPanelProps {
  // ─── Render flags ─────────────────────────────────────────────────────────
  /** Current render-flag state. Each key defaults to: orbits=on, labels=off, coverage=off. */
  layers?: RenderLayers;
  /** Called with the next render-flag state when any toggle is clicked. */
  onLayersChange?: (next: RenderLayers) => void;

  // ─── Object-group visibility ──────────────────────────────────────────────
  /** Current group-visibility state. Each key defaults to true (visible). */
  visibility?: GroupVisibility;
  /** Called with the next visibility state when any toggle is clicked. */
  onVisibilityChange?: (next: GroupVisibility) => void;
  /** Counts to render in group toggle labels (e.g. "Spacecraft (5)"). */
  groupCounts?: GroupCounts;

  // ─── Custom layers (app-defined overlays) ─────────────────────────────────
  /** App-defined layer toggles. The panel only renders the toggle UI. */
  customLayers?: LayerDef[];
  /** Current state of each custom layer (id → on/off). */
  customLayerState?: Record<string, boolean>;
  /** Fires when a custom layer is toggled. The consumer renders the overlay itself. */
  onCustomLayerChange?: (id: string, enabled: boolean) => void;

  // ─── Layout / theming ─────────────────────────────────────────────────────
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  offset?: { x?: number; y?: number };
  hidden?: boolean;
  style?: CSSProperties;

  /** Hide the render-flags section (`true` to suppress). */
  hideRenderFlags?: boolean;
  /** Hide the object-group section. */
  hideGroups?: boolean;
  /** Hide the custom-layers section even if `customLayers` is non-empty. */
  hideCustomLayers?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const LayerControlPanel = memo(function LayerControlPanel({
  layers,
  onLayersChange,
  visibility,
  onVisibilityChange,
  groupCounts,
  customLayers,
  customLayerState,
  onCustomLayerChange,
  position = "top-right",
  offset,
  hidden = false,
  style,
  hideRenderFlags = false,
  hideGroups = false,
  hideCustomLayers = false,
}: LayerControlPanelProps) {
  const tokens = useThemeTokens();

  // Default render-flag state — matches ZenSpace3D's defaults.
  const showLabels = layers?.labels === true;
  const showCoverage = layers?.coverage === true;
  const showOrbits = layers?.orbits !== false;

  // Default group-visibility — matches ZenSpace3D's defaults (everything on).
  const showSpacecraft = visibility?.spacecraft !== false;
  const showGroundStations = visibility?.groundStations !== false;
  const showCelestialBodies = visibility?.celestialBodies !== false;

  const toggleLayer = useCallback(
    (key: keyof RenderLayers) => {
      if (!onLayersChange) return;
      const current =
        key === "orbits"
          ? showOrbits
          : key === "labels"
            ? showLabels
            : showCoverage;
      onLayersChange({ ...(layers ?? {}), [key]: !current });
    },
    [layers, onLayersChange, showLabels, showCoverage, showOrbits],
  );

  const toggleVisibility = useCallback(
    (key: keyof GroupVisibility) => {
      if (!onVisibilityChange) return;
      const current =
        key === "spacecraft"
          ? showSpacecraft
          : key === "groundStations"
            ? showGroundStations
            : showCelestialBodies;
      onVisibilityChange({ ...(visibility ?? {}), [key]: !current });
    },
    [
      visibility,
      onVisibilityChange,
      showSpacecraft,
      showGroundStations,
      showCelestialBodies,
    ],
  );

  const offX = offset?.x ?? 16;
  const offY = offset?.y ?? 16;
  const anchor: CSSProperties = (() => {
    switch (position) {
      case "top-left":
        return { top: offY, left: offX };
      case "bottom-left":
        return { bottom: offY, left: offX };
      case "bottom-right":
        return { bottom: offY, right: offX };
      case "top-right":
      default:
        return { top: offY, right: offX };
    }
  })();

  if (hidden) return null;

  const showRender = !hideRenderFlags && onLayersChange != null;
  const showGroupsSection = !hideGroups && onVisibilityChange != null;
  const showCustom = !hideCustomLayers && (customLayers?.length ?? 0) > 0;

  if (!showRender && !showGroupsSection && !showCustom) return null;

  const cnt = (n: number | undefined) =>
    typeof n === "number" ? ` (${n})` : "";

  return (
    <div
      data-testid="zendir-layer-control"
      style={{
        position: "absolute",
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: tokens.spacing.sm,
        borderRadius: tokens.borderRadius?.md ?? 6,
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(0,0,0,0.72)",
        color: tokens.colors.text.primary,
        fontSize: tokens.typography.fontSize.xs,
        lineHeight: 1.2,
        ...anchor,
        ...style,
      }}
    >
      {showRender && (
        <Section title="Render">
          <Toggle
            label="Orbits"
            active={showOrbits}
            onClick={() => toggleLayer("orbits")}
          />
          <Toggle
            label="Labels"
            active={showLabels}
            onClick={() => toggleLayer("labels")}
          />
          <Toggle
            label="Coverage"
            active={showCoverage}
            onClick={() => toggleLayer("coverage")}
          />
        </Section>
      )}

      {showGroupsSection && (
        <Section title="Objects">
          <Toggle
            label={`Spacecraft${cnt(groupCounts?.spacecraft)}`}
            active={showSpacecraft}
            onClick={() => toggleVisibility("spacecraft")}
          />
          <Toggle
            label={`Ground Stations${cnt(groupCounts?.groundStations)}`}
            active={showGroundStations}
            onClick={() => toggleVisibility("groundStations")}
          />
          <Toggle
            label={`Celestial${cnt(groupCounts?.celestialBodies)}`}
            active={showCelestialBodies}
            onClick={() => toggleVisibility("celestialBodies")}
          />
        </Section>
      )}

      {showCustom && (
        <Section title="Overlays">
          {(customLayers ?? []).map((layer) => (
            <Toggle
              key={layer.id}
              label={layer.label}
              active={
                customLayerState?.[layer.id] ?? layer.defaultEnabled ?? false
              }
              onClick={() => {
                const current =
                  customLayerState?.[layer.id] ?? layer.defaultEnabled ?? false;
                onCustomLayerChange?.(layer.id, !current);
              }}
            />
          ))}
        </Section>
      )}
    </div>
  );
});

// ─── Sub-components ───────────────────────────────────────────────────────────

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  const tokens = useThemeTokens();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div
        style={{
          opacity: 0.6,
          fontSize: "0.85em",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: tokens.colors.text.secondary,
        }}
      >
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {children}
      </div>
    </div>
  );
};

const Toggle = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => {
  const tokens = useThemeTokens();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: `${tokens.spacing.xs}px ${tokens.spacing.sm}px`,
        borderRadius: tokens.borderRadius?.sm ?? 4,
        background: active ? "rgba(100,200,255,0.2)" : "rgba(0,0,0,0.6)",
        border: `1px solid ${active ? "rgba(100,200,255,0.6)" : "rgba(255,255,255,0.15)"}`,
        color: active ? "rgba(100,200,255,1)" : tokens.colors.text.secondary,
        fontSize: tokens.typography.fontSize.xs,
        cursor: "pointer",
        letterSpacing: 0.3,
        textAlign: "left",
      }}
    >
      {label}
    </button>
  );
};

export default LayerControlPanel;
